import {
  AppBar,
  Container,
  Grid,
  makeStyles,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import React, { useContext } from 'react';
import Badge from '@material-ui/core/Badge';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { useAuth } from '../contexts/AuthContext';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: 'white',
    borderBottom: '1px solid #dcdcdc',
    marginBottom: '0rem',
  },
  navLogo: {
    [theme.breakpoints.down('sm')]: {
      margin: '10px auto 0',
    },
    '& a': {
      textDecoration: 'none',
    },
  },
  navLogoImage: {
    [theme.breakpoints.down('sm')]: {
      maxHeight: 30,
    },
    maxHeight: 50,
  },
  textLogo: {
    fontSize: '1.5rem',
    lineHeight: '2.25rem',
    fontWeight: '700',
    color: '#334155',
    letterSpacing: '0.05em',
  },
  navLinks: {
    paddingTop: '10px',
    [theme.breakpoints.down('sm')]: {
      margin: '8px auto 10px',
    },
  },
  navLink: {
    [theme.breakpoints.down('sm')]: {
      height: 48,
    },
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#555',
    marginRight: '.8rem',
    '&:hover': {
      color: [theme.palette.secondary.light],
    },
  },
  lastNavLink: { marginRight: 0 },
  navItem: {},
  active: {
    color: [theme.palette.secondary.main],
  },
  userIcon: {
    color: '#555',
    '&:hover': {
      color: [theme.palette.secondary.light],
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '.2rem',
      marginTop: '-6px',
    },
  },
  userIconActive: {
    color: [theme.palette.secondary.main],
  },
  userIconRing: {
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: '50%',
    padding: '0 !important',
    minWidth: '0 !important',
    minHeight: '0 !important',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    marginLeft: 0,
    marginTop: 0,
    background: 'white',
    overflow: 'visible',
    '& .MuiIconButton-label': {
      padding: 0,
      minWidth: 0,
      minHeight: 0,
    },
    '&:hover': {
      borderColor: theme.palette.secondary.light,
      color: theme.palette.secondary.light,
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginTop: 0,
    },
  },
  userIconNoRing: {
    border: 'none',
    borderRadius: '50%',
    padding: '0 !important',
    minWidth: '0 !important',
    minHeight: '0 !important',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    marginLeft: 0,
    marginTop: 0,
    background: 'white',
    overflow: 'visible',
    '& .MuiIconButton-label': {
      padding: 0,
      minWidth: 0,
      minHeight: 0,
    },
    '&:hover': {
      color: theme.palette.secondary.light,
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginTop: 0,
    },
  },
}));

export default function Navbar() {
  const location = useLocation();
  const items = useSelector((s) => s.cart.items);
  const lab = useSelector((s) => s.lab);
  const { isAuthenticated } = useAuth();
  const MenuItems = [
    {
      text: 'Home',
      path: '/',
    },
    {
      text: 'Help',
      path: '/help',
    },
    {
      text: 'Contact',
      path: '/contact',
    },
    {
      render: () => (
        <Badge
          max={9999}
          badgeContent={items.length}
          color="secondary"
          overlap="rectangular"
        >
          <Link
            to="/cart"
            key="Cart"
            className={`${classes.navLink} ${
              location.pathname === '/cart' ? classes.active : null
            }`}
          >
            Cart
          </Link>
        </Badge>
      ),
    },
    {
      render: () =>
        isAuthenticated ? (
          <Tooltip title="Logged in" arrow>
            <IconButton
              component={Link}
              to="/profile"
              className={`${classes.userIconRing} ${
                location.pathname === '/profile'
                  ? classes.userIconActive
                  : classes.userIcon
              }`}
              aria-label="user profile"
              size="small"
              disableRipple
              disableFocusRipple
            >
              <AccountCircleIcon style={{ fontSize: '24px' }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Not logged in" arrow>
            <IconButton
              component={Link}
              to="/profile"
              className={`${classes.userIconNoRing} ${
                location.pathname === '/profile'
                  ? classes.userIconActive
                  : classes.userIcon
              }`}
              aria-label="user profile"
              size="small"
            >
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
        ),
    },
  ];
  const classes = useStyles();

  return (
    <AppBar position="relative" className={classes.root} elevation={0}>
      <Container fixed>
        <Toolbar disableGutters>
          <Grid container justifyContent="space-between">
            <Grid item key="logo" className={classes.navLogo}>
              <Link to="/">
                {lab.lab_name === 'WalletSizePrints by ProLabPrints.com' ? (
                  <Typography variant="h2" className={classes.textLogo}>
                    WalletSize<span style={{ color: '#ea580c' }}>Prints</span>
                  </Typography>
                ) : (
                  <img
                    className={classes.navLogoImage}
                    src={lab.lab_logo}
                    alt={lab.lab_name}
                  />
                )}
              </Link>
            </Grid>
            <Grid item key="links" className={classes.navLinks}>
              <Grid container justifyContent="center">
                {MenuItems.map((item, idx) => (
                  <Grid item className={classes.navLink} key={idx}>
                    {item.render ? (
                      item.render()
                    ) : (
                      <Link
                        to={item.path}
                        key={item.text}
                        className={`${classes.navLink} ${
                          location.pathname === item.path
                            ? classes.active
                            : null
                        }`}
                      >
                        {item.text}
                      </Link>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
